import { useEffect, useState } from 'react';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Alert } from '@/components/UI/Alert';
import { Modal } from '@/components/UI/Modal';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { useListAuditEventsQuery, type AuditEvent } from '@/redux/internal-services/platformLogsApi';
import {
  DetailRow,
  JsonBlock,
  PAGE_SIZE,
  PagedFooter,
  TraceLink,
  fmtTime,
  toIso,
  type SharedLogFilters,
} from './shared';

const EVENT_VARIANT: Record<string, 'success' | 'accent' | 'danger' | 'neutral'> = {
  created: 'success',
  bulk_created: 'success',
  updated: 'accent',
  bulk_updated: 'accent',
  replaced: 'accent',
  deleted: 'danger',
  bulk_deleted: 'danger',
};

function ChangesTable({ event }: { event: AuditEvent }) {
  const fields = event.changed_fields.length ? event.changed_fields : Object.keys(event.new_values);
  if (!fields.length) return null;
  const show = (v: unknown) => (v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v));
  return (
    <div className="table-card overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr>
            <th className="th-cell">Field</th>
            <th className="th-cell">Before</th>
            <th className="th-cell">After</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f}>
              <td className="td-cell font-mono text-xs">{f}</td>
              <td className="td-cell break-all font-mono text-xs text-ui-text3">{show(event.previous_values[f])}</td>
              <td className="td-cell break-all font-mono text-xs text-ui-text">{show(event.new_values[f])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AuditLogTab({ tenantId, fromDate, toDate, onTrace }: SharedLogFilters) {
  const [module, setModule] = useState('');
  const [eventType, setEventType] = useState('');
  const [actor, setActor] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  useEffect(() => setSkip(0), [tenantId, fromDate, toDate, module, eventType, actor, resourceId]);

  const { data, isFetching, isError } = useListAuditEventsQuery({
    tenant_id: tenantId || undefined,
    from_date: toIso(fromDate),
    to_date: toIso(toDate),
    module: module || undefined,
    event_type: eventType || undefined,
    actor_user_id: actor || undefined,
    resource_id: resourceId || undefined,
    limit: PAGE_SIZE,
    skip,
  });
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <input aria-label="Filter by module" placeholder="Module (e.g. tasks)" value={module}
          onChange={(e) => setModule(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-40')} />
        <select aria-label="Filter by action" value={eventType} onChange={(e) => setEventType(e.target.value)}
          className={cn(fieldClass({ size: 'sm' }), 'w-auto')}>
          <option value="">All actions</option>
          {Object.keys(EVENT_VARIANT).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input aria-label="Filter by user ID" placeholder="User ID" value={actor}
          onChange={(e) => setActor(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-48')} />
        <input aria-label="Filter by record ID" placeholder="Record ID" value={resourceId}
          onChange={(e) => setResourceId(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-48')} />
        <span className="text-xs text-ui-text3">{total} results</span>
      </div>

      {isError && <Alert tone="danger">Could not load audit events.</Alert>}

      <div className="table-card">
        {isFetching ? (
          <LoadingState label="Loading..." />
        ) : rows.length === 0 ? (
          <EmptyState title="No audit events match these filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th-cell">Time</th>
                  <th className="th-cell">Action</th>
                  <th className="th-cell">Module</th>
                  <th className="th-cell">Record</th>
                  <th className="th-cell">User</th>
                  <th className="th-cell">Tenant</th>
                  <th className="th-cell">Trace</th>
                  <th className="th-cell" />
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="tr-hover">
                    <td className="td-cell whitespace-nowrap text-xs text-ui-text2">{fmtTime(e.occurred_at)}</td>
                    <td className="td-cell"><Badge variant={EVENT_VARIANT[e.event_type] ?? 'neutral'}>{e.event_type}</Badge></td>
                    <td className="td-cell text-xs">{e.module}</td>
                    <td className="td-cell font-mono text-xs text-ui-text2">
                      {e.resource_type}{e.resource_id ? ` · ${e.resource_id}` : ''}
                    </td>
                    <td className="td-cell font-mono text-xs text-ui-text3">{e.actor_user_id}</td>
                    <td className="td-cell font-mono text-xs text-ui-text3">{e.tenant_id || '—'}</td>
                    <td className="td-cell"><TraceLink id={e.correlation_id} onTrace={onTrace} /></td>
                    <td className="td-cell">
                      <Button variant="link" size="xs" className="text-accent-text" onClick={() => setSelected(e)}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PagedFooter skip={skip} total={total} onSkip={setSkip} />

      {selected && (
        <Modal title="Audit event" onClose={() => setSelected(null)} maxWidthClassName="sm:max-w-3xl">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailRow label="Time">{fmtTime(selected.occurred_at)}</DetailRow>
            <DetailRow label="Action">{selected.event_type} ({selected.outcome})</DetailRow>
            <DetailRow label="Module">{selected.module}</DetailRow>
            <DetailRow label="Record">{selected.resource_type} {selected.resource_id}</DetailRow>
            <DetailRow label="User">{selected.actor_user_id}</DetailRow>
            <DetailRow label="Tenant">{selected.tenant_id}</DetailRow>
            <DetailRow label="Trace">
              <TraceLink id={selected.correlation_id} onTrace={(id) => { setSelected(null); onTrace(id); }} />
            </DetailRow>
          </div>
          <ChangesTable event={selected} />
          {Object.keys(selected.metadata).length > 0 && <JsonBlock value={selected.metadata} />}
        </Modal>
      )}
    </div>
  );
}
